import { useId, useRef, useState } from 'react'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'

export type ConfirmTone = 'default' | 'danger'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
  onConfirm: () => void
  onCancel: () => void
  /**
   * Quando informado, «Confirmar» marca `current = true` e só chama `onConfirm` depois de a entrada
   * de histórico do diálogo ser consumida (BACK-07: quem confirma pode navegar sem passo fantasma).
   */
  confirmedRef?: React.MutableRefObject<boolean>
}

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'default',
  onCancel,
  onConfirm,
  confirmedRef,
}: ConfirmDialogProps) => {
  const titleId = useId()
  const descriptionId = useId()
  const cancelRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [closing, setClosing] = useState(false)
  if (!open && closing) setClosing(false)
  const visible = open && !closing

  // BACK-03/17: voltar e Esc = Cancelar, só no topo da pilha única; foco preso no painel.
  const { closeThen } = useOverlay(visible, {
    kind: 'alertdialog',
    onRequestClose: (reason) => {
      if (reason !== 'ui') onCancel()
    },
    panelRef,
    initialFocusRef: cancelRef,
  })

  const handleConfirm = () => {
    if (!confirmedRef) {
      onConfirm()
      return
    }
    confirmedRef.current = true
    setClosing(true)
    closeThen(onConfirm)
  }

  if (!visible) return null

  return (
    <div
      className="fixed inset-0 z-[150] flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel()
        }
      }}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className="w-full max-w-[440px]"
      >
        <Card className="space-y-3 rounded-[var(--radius)] p-4 shadow-[0_18px_45px_rgba(31,43,40,0.22)]">
          <div className="space-y-1.5">
            <h2 id={titleId} className="text-base font-bold text-[var(--color-text-primary)]">
              {title}
            </h2>
            {description ? (
              <p
                id={descriptionId}
                aria-live="assertive"
                className="text-sm text-[var(--color-text-secondary)]"
              >
                {description}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button ref={cancelRef} variant="ghost" onClick={onCancel} className="sm:min-w-32">
              {cancelLabel}
            </Button>
            <button
              type="button"
              onClick={handleConfirm}
              className={
                tone === 'danger'
                  ? 'min-h-10 rounded-lg bg-[var(--color-wine-soft)] px-4 text-sm font-semibold text-[var(--color-wine-strong)] transition-all duration-200 hover:bg-[var(--color-wine-soft-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-wine-strong)] focus-visible:ring-offset-2 active:scale-[0.99]'
                  : 'min-h-10 rounded-lg bg-[var(--color-mint-strong)] px-4 text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint-ring)] focus-visible:ring-offset-2 active:scale-[0.99]'
              }
            >
              {confirmLabel}
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}
